import type { ApplyDiscountInput, Booking, BookingGarment } from '../api/bookings';
import { canAct } from '../session/capability';
import type { Session } from '../session/types';
import { isTotalWithinCeiling, resolveAll, type DiscountInput } from './discount';
import { isOffTheBooking } from './tones';

/**
 * Giving, changing or taking back the discount on a booking that already exists (spec Section 4) —
 * the form's rules, for the website's discount drawer and the phone's discount screen.
 *
 * <p>
 * The arithmetic is `discount.ts`'s. What lives here is everything around it that both screens had
 * to agree on: when a discount may be offered at all, how a figure typed into a box becomes a
 * concession, when a reason is owed, and the request — so the phone cannot send a request the
 * website would not.
 * </p>
 *
 * <p>
 * <strong>The ceiling is read off the booking, never the session.</strong> `discount.ceilingPct` is
 * resolved server-side for this user at this booking's branch: a manager at head office is a clerk at
 * the second shop. <strong>Removing is never bounded by it</strong> — somebody who may not grant 20%
 * must still be able to take a mistyped 20% back off, and the server agrees.
 * </p>
 */

/** The garments the booking is actually for — amended-off tombstones excluded. */
export function liveGarments(booking: Booking): BookingGarment[] {
  return booking.wearers.flatMap((wearer) =>
    wearer.garments.filter((garment) => !isOffTheBooking(garment.state)),
  );
}

/**
 * Whether to offer the discount at all. Its own grant (`RentalOrder:Discount`), not the
 * `RentalOrder:Edit` the rest of a booking's moves sit behind — arranging garments and giving money
 * away are different authorities. Live bookings only, including one already out: the customer who
 * finds a mark on the sherwani is at the counter with it in their hands. The server refuses the three
 * closed stages.
 */
export function canDiscount(session: Session, booking: Pick<Booking, 'stage'>): boolean {
  return (
    canAct(session, 'RentalOrder:Discount') &&
    booking.stage !== 'Completed' &&
    booking.stage !== 'Cancelled' &&
    booking.stage !== 'Expired'
  );
}

/** "10% off" and "make it nine thousand" are one concession, said two ways. */
export type DiscountMode = 'pct' | 'amount';

/** The form as typed. The two boxes are strings, because an empty box is not zero. */
export interface DiscountForm {
  mode: DiscountMode;
  pct: string;
  amount: string;
  reason: string;
  /** Every live garment's own concession, keyed by line id. */
  lines: Record<number, DiscountInput>;
}

/**
 * The form seeded from what is on the booking, so opening it to change a reason does not mean
 * retyping the figure. A flat sum comes back as a flat sum: re-seeding it as its effective
 * percentage would silently turn it into one that follows the party.
 */
export function discountFormFor(booking: Booking): DiscountForm {
  const { discount } = booking;

  return {
    mode: discount.pct !== null ? 'pct' : 'amount',
    pct: discount.pct === null ? '' : String(discount.pct),
    amount: discount.amount === null ? '' : String(discount.amount),
    reason: discount.reason ?? '',
    lines: Object.fromEntries(
      liveGarments(booking).map((garment) => [
        garment.id,
        { pct: garment.discountPct, amount: garment.discountAmount },
      ]),
    ),
  };
}

export interface DiscountCheck {
  /** Both levels, composed as the server composes them. */
  resolved: ReturnType<typeof resolveAll<number>>;
  /** Whether the booking-level box holds a number. */
  entered: boolean;
  typed: number;
  /** Over this user's ceiling at this branch, measured on the combined figure. */
  over: boolean;
  negative: boolean;
  /** Anything given at either level — the condition a reason is owed on. */
  givingSomething: boolean;
  /** The one sentence to show when it cannot be applied, or null when it can. */
  problem: string | null;
}

/** What the form comes to, and whether the server would take it. */
export function checkDiscount(booking: Booking, form: DiscountForm): DiscountCheck {
  const text = (form.mode === 'pct' ? form.pct : form.amount).trim();
  const typed = Number(text);
  const entered = text !== '' && Number.isFinite(typed);

  const resolved = resolveAll(
    liveGarments(booking).map((garment) => ({
      key: garment.id,
      rate: garment.rentalRate ?? 0,
      input: form.lines[garment.id] ?? { pct: null, amount: null },
    })),
    entered
      ? {
          pct: form.mode === 'pct' ? typed : null,
          amount: form.mode === 'amount' ? typed : null,
        }
      : { pct: null, amount: null },
  );

  const over = !isTotalWithinCeiling(resolved, booking.discount.ceilingPct);
  const negative = entered && typed < 0;
  const givingSomething = resolved.total > 0;

  // A reason is compulsory whenever anything is given, and irrelevant when it is being cleared —
  // the server does not read it on a withdrawal.
  const problem = negative
    ? 'A discount cannot be negative. To charge more, change the rate card.'
    : over
      ? 'That is over your limit.'
      : givingSomething && form.reason.trim() === ''
        ? 'Say why it was given.'
        : null;

  return { resolved, entered, typed, over, negative, givingSomething, problem };
}

/**
 * The request for the form. **Every live garment's line goes with it, always**: the endpoint reads a
 * list as "these are the line concessions now", so sending only the changed ones would clear the
 * rest.
 */
export function discountRequest(booking: Booking, form: DiscountForm): ApplyDiscountInput {
  const { entered, typed } = checkDiscount(booking, form);

  return {
    pct: entered && form.mode === 'pct' ? typed : null,
    amount: entered && form.mode === 'amount' ? typed : null,
    lines: liveGarments(booking).map((garment) => ({
      garmentId: garment.id,
      pct: form.lines[garment.id]?.pct ?? null,
      amount: form.lines[garment.id]?.amount ?? null,
    })),
    reason: form.reason.trim() || null,
  };
}

/** Takes both levels off at once — what "remove the discount" means to whoever presses it. */
export const removeDiscountRequest: ApplyDiscountInput = { pct: null, amount: null, lines: [] };

/**
 * The reasons a counter gives most, offered as chips with "Other" for typing
 * (`docs/Mobile_Redesign_Plan.md`, decision 3 — provisional). A chip fills the same compulsory
 * reason the typed field does, so the server sees no difference.
 */
export const discountReasons: readonly string[] = [
  'Regular customer',
  'Whole wedding party',
  'Price match',
];

/**
 * The one-tap percentages — 5, 10 and 15 — **only those within this person's ceiling**, so a chip
 * never offers a discount the server will refuse. A larger figure is typed, and refused by
 * `checkDiscount` like any other.
 */
export function quickPercents(ceilingPct: number): number[] {
  return [5, 10, 15].filter((pct) => pct <= ceilingPct);
}
